import * as React from "react";
import { Input } from "@/components/ui/input";

export interface ABNInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "onKeyDown"
  > {
  value: string;
  onChange: (value: string) => void;
}

export const ABNInput = React.forwardRef<HTMLInputElement, ABNInputProps>(
  ({ value, onChange, ...props }, ref) => {
    const formatABN = (val: string) => {
      const cleaned = val.replace(/\D/g, "").slice(0, 11);
      let formatted = "";
      for (let i = 0; i < cleaned.length; i++) {
        formatted += cleaned[i];
        if (i === 1 || i === 4 || i === 7) {
          formatted += " ";
        }
      }
      return formatted;
    };

    const getRawFromFormatted = (formatted: string) =>
      formatted.replace(/\D/g, "").slice(0, 11);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Backspace") return;

      const input = e.currentTarget;
      const pos = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;

      if (pos !== end) return;
      if (pos === 0) return;

      if (input.value[pos - 1] === " ") {
        e.preventDefault();
        const newFormatted =
          input.value.slice(0, pos - 2) + input.value.slice(pos);
        const newRaw = getRawFromFormatted(newFormatted);
        onChange(newRaw);
        window.requestAnimationFrame(() => {
          if (ref && typeof ref === "object" && ref.current) {
            ref.current.selectionStart = ref.current.selectionEnd = pos - 2;
          }
        });
        return;
      }

      e.preventDefault();
      const newFormatted =
        input.value.slice(0, pos - 1) + input.value.slice(pos);
      const newRaw = getRawFromFormatted(newFormatted);
      onChange(newRaw);
      window.requestAnimationFrame(() => {
        if (ref && typeof ref === "object" && ref.current) {
          ref.current.selectionStart = ref.current.selectionEnd = pos - 1;
        }
      });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = getRawFromFormatted(e.target.value);
      onChange(raw);
    };

    return (
      <Input
        {...props}
        ref={ref}
        value={formatABN(value || "")}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        inputMode="numeric"
        maxLength={14} // 11 digits + 3 spaces
      />
    );
  }
);

ABNInput.displayName = "ABNInput";
